import { presentations as presentationsApi } from "apis/callables";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import PresentationsLinksAdapter from "js/core/storage/presentationLinksAdapter";
import { LinkType } from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import moment from "moment";
import StorageModel from "../storage/storageModel";

const logger = getLogger(LogGroup.PRESENTATION);

const PresentationLink = StorageModel.extend({
    root: "presentationLinks",
    ensurePersisted: true,

    initialize: function(model, options) {
        this.presentationId = options.presentationId;
    },

    createAdapter: function(options) {
        return new PresentationsLinksAdapter({ ...options, model: this, presentationId: this.presentationId });
    },

    getLinkURL: function() {
        const customPlayerURLTemplate = WorkspaceController.workspace?.player.settings.customPlayerURLTemplate;
        if (customPlayerURLTemplate) {
            return customPlayerURLTemplate.replace("${linkId}", this.id);
        } else {
            switch (this.get("type")) {
                case LinkType.MEETING:
                    return `${window.location.origin}/meeting/${this.id}`;
                case LinkType.EMBED:
                    return `${window.location.origin}/embed/${this.id}`;
                default:
                    return `${window.location.origin}/player/${this.id}`;
            }
        }
    },

    // checks if a link has an expiration set
    shouldLinkExpire() {
        return !!this.get("expiresAt");
    },

    // checks if this link is expired or not
    isLinkExpired() {
        return this.isExpirationBeforeDate(new Date());
    },

    // checks if this link has expired or not
    isExpirationBeforeDate(date) {
        const expiresAt = this.get("expiresAt");

        // not set to expire
        if (!expiresAt) {
            return false;
        }

        // check if this falls before now
        const expires = moment(expiresAt);
        return expires.isBefore(date);
    },

    getInviteContactName: function() {
        let contact = this.get("email");
        if (typeof (contact) == "object") {
            return contact.name;
        } else {
            return contact;
        }
    },

    update: function(attrs, options) {
        // add `modifiedAt` on every update (if the update actually changes anything)
        let changeSet = StorageModel.prototype.update.call(this, attrs, Object.assign({ computeChangeSet: true }, options));
        if (changeSet.hasUpdates) {
            attrs = Object.assign({ modifiedAt: new Date().getTime() }, attrs);
        }
        return StorageModel.prototype.update.call(this, attrs, options);
    },

    updatePassword: async function(password) {
        if (this.get("type") !== LinkType.PROTECTED) return;

        await presentationsApi.updateLinkPassword({
            id: this.get("presentationId"), linkId: this.id, password
        });
    },

    connectEmbedDomains: async function(domainWhitelist) {
        if (this.get("type") !== LinkType.EMBED || !domainWhitelist) {
            return;
        }

        await presentationsApi.connectEmbedLinkDomains({
            id: this.get("presentationId"), linkId: this.id, domainWhitelist
        });
    },

    getLinkName: function() {
        switch (this.get("type")) {
            case LinkType.INVITE:
                return this.getInviteContactName();
            case LinkType.SOCIAL:
                return this.get("name").toTitleCase();
            default:
                return this.get("name");
        }
    }
});

/**
 * Class that mimics the old PresentationLinks reference collection
 */
class PresentationLinks {
    constructor(presentation) {
        this.presentation = presentation;

        this.models = [];
        this.loadPromise = null;

        this.handlePresentationChange = () => {
            const presentationLinkIds = this.presentationLinkIds;

            this.models.forEach(model => {
                if (!presentationLinkIds.includes(model.id)) {
                    this._unloadLink(model.id);
                }
            });

            presentationLinkIds.forEach(linkId => {
                if (!this.models.some(model => model.id === linkId)) {
                    this._loadLink(linkId)
                        .catch(err => {
                            logger.error(`[PresentationLinks] addLink() error adding link ${linkId}`, err);
                        });
                }
            });
        };
    }

    get presentationLinkIds() {
        return Object.keys(this.presentation.get("links") ?? {}).filter(key => !["_changeId", "modifiedAt", "createdAt", "id"].includes(key));
    }

    async _loadLink(linkId) {
        const linkModel = new PresentationLink({ id: linkId }, { presentationId: this.presentation.id, autoSync: !this.presentation.disconnected });
        this.models = [...this.models.filter(model => model.id !== linkId), linkModel];
        await linkModel.load();
        return linkModel;
    }

    _unloadLink(linkId) {
        const model = this.models.find(model => model.id === linkId);
        if (model) {
            model.disconnect();
        }
        this.models = this.models.filter(model => model.id !== linkId);
    }

    load() {
        if (!this.loadPromise) {
            this.loadPromise = (async () => {
                this.presentation.on("change", this.handlePresentationChange);
                await Promise.all(this.presentationLinkIds.map(linkId => this._loadLink(linkId)));
            })();
        }
        return this.loadPromise;
    }

    async loadModels() {
        return this.load();
    }

    /**
     * Creates a new link, DOES NOT ADD IT TO THE PRESENTATION
     */
    async createLink(model) {
        // Using the api directly as the id has to be generated by the server,
        // and our StorageModel doesn't support async id generation
        const { id } = await presentationsApi.createLink({
            id: this.presentation.id,
            link: {
                ...model,
                presentationId: this.presentation.id
            }
        });

        const linkModel = new PresentationLink({ id }, { presentationId: this.presentation.id, autoSync: !this.presentation.disconnected });
        await linkModel.load();
        return linkModel;
    }

    /**
     * Adds link to the presentation, ensures it's loaded
     */
    async addLink(link) {
        this.models = [...this.models.filter(model => model.id !== link.id), link];

        await link.load();

        this.presentation.update({
            links: {
                [link.id]: true
            }
        });
        await this.presentation.updatePromise;

        return link;
    }

    /**
     * Deletes link from the presentation, ensures it's unloaded
     */
    async deleteLink(linkId) {
        this._unloadLink(linkId);

        await this.presentation.update({
            links: {
                [linkId]: null
            }
        });

        await presentationsApi.deleteLink({
            id: this.presentation.id,
            linkId
        });
    }

    get(linkId) {
        return this.models.find(model => model.id === linkId);
    }

    find(predicate) {
        return this.models.find(predicate);
    }

    filter(predicate) {
        return this.models.filter(predicate);
    }

    disconnect() {
        this.presentation.off("change", this.handlePresentationChange);
        this.models.forEach(model => model.disconnect());
        this.models = [];
        this.loadPromise = null;
    }
}

export { PresentationLink, PresentationLinks };
